<template>
  <v-dialog
    scrollable
    width="480"
    persistent
    :value="show"
    ata-testid="teamForm-dialog"
  >
    <v-card class="py-6">
      <v-card-title data-testid="teamForm-text-title" class="h6 pa-0 px-6">
        {{ isNew ? $t('teams.create_team') : $t('teams.update_team') }}
      </v-card-title>

      <v-card-subtitle
        data-testid="teamForm-text-subtitle"
        v-if="isNew"
        class="blue-grey--text text--darken-2 pa-0 mt-2 px-6"
      >
        {{ $t('teams.create_team_subtitle') }}
      </v-card-subtitle>

      <v-card-text class="px-0 py-1 mx-0 mt-4" style="overflow-x: visible;">
        <div class="px-6">
          <v-form v-model="isFormValid" ref="form" lazy-validation>
            <v-text-field
              data-testid="teamForm-input-name"
              v-if="permissions.create || permissions.editTeamInfo"
              v-model="form.name"
              autofocus
              data-private
              dense
              outlined
              class="mt-0 mb-n2"
              :disabled="isLoading"
              :label="`${$t('common.name')}*`"
              :loading="isLoading"
              :rules="[
                name =>
                  Boolean(name.trim()) ||
                  $t('validation.form.this_field_is_required'),
              ]"
            />

            <v-textarea
              data-testid="teamForm-text-description"
              v-if="permissions.create || permissions.editTeamInfo"
              v-model="form.description"
              auto-grow
              data-private
              dense
              no-resize
              outlined
              class="mt-0"
              rows="1"
              style="white-space: pre-wrap; word-break: break-word;"
              :maxlength="MAX_DESCRIPTION_CHARACTERS"
              :counter="MAX_DESCRIPTION_CHARACTERS"
              :disabled="isLoading"
              :label="`${$t('common.description')}`"
              :loading="isLoading"
              :rules="[
                description =>
                  description.length <= MAX_DESCRIPTION_CHARACTERS ||
                  $t('validation.form.max_characters_exceeded'),
              ]"
            />
            <div v-if="permissions.setGlobalTeam">
              <div class="d-flex align-center mb-2">
                <v-switch
                  data-testid="teamForm-switch-isGlobalTeam"
                  class="my-0 pa-0 mx-2"
                  hide-details
                  :label="$t('teams.global_team')"
                  :input-value="form.site === null"
                  @change="handleToggleGlobalTeam"
                ></v-switch>

                <v-tooltip right max-width="200">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" color="blue-grey"
                      >mdi-help-circle-outline</v-icon
                    >
                  </template>

                  {{ $t('teams.global_team_helper') }}
                </v-tooltip>
              </div>
              <SingleSelectionAutocomplete
                data-testid="teamForm-selector-site"
                v-if="form.site"
                dense
                hide-details
                outlined
                class="mt-4 mb-4"
                item-value="id"
                item-text="name"
                :required="false"
                :value="form.site"
                :options="sites"
                :label="$t('common.site_one')"
                :loading="isLoading"
                @change="onSiteChange"
              />
            </div>
            <AssigneesSelector
              required
              multiple
              showAdvancedOption
              :blacklist="form.members"
              :selectedAccountIds="form.leaders"
              :disabled="
                isLoading ||
                  (!permissions.editTeamLeaders && !permissions.create)
              "
              :label="`${$t('teams.leader_other')}*`"
              :buttonText="$t('teams.add_leader')"
              @change="handleLeadersChange"
            />
            <AssigneesSelector
              v-if="permissions.editTeamMembers || permissions.create"
              required
              multiple
              showAdvancedOption
              class="mt-1"
              :blacklist="form.leaders"
              :selectedAccountIds="form.members"
              :disabled="isLoading"
              :label="`${$t('common.member_other')}*`"
              :buttonText="$t('teams.add_members')"
              @change="handleMembersChange"
            />
          </v-form>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end ma-0 mt-6 pa-0 px-6">
        <Button
          data-testid="teamForm-button-cancel"
          color="grey"
          plain
          :text="$t('common.button.cancel')"
          @click="$emit('close')"
        />

        <Button
          data-testid="teamForm-button-save"
          color="primary"
          :disabled="!permissions.create && !permissions.editTeamMembers"
          :loading="isLoading"
          :text="isNew ? $t('common.button.create') : $t('common.button.save')"
          @click="saveTeam"
        />
      </v-card-actions>
    </v-card>

    <v-dialog persistent :value="isSiteTeamWarningVisible" max-width="320">
      <v-card>
        <v-card-title>
          {{ $t('teams.site_team_confirmation_title') }}
        </v-card-title>
        <v-card-text>
          {{ $t('teams.site_team_confirmation_message') }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <Button
            color="grey"
            plain
            :text="$t('common.button.cancel')"
            @click="cancelChangeToSiteTeam"
          />

          <Button
            plain
            color="danger"
            :text="$t('common.button.continue')"
            @click="confirmChangeToSiteTeam"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import teamPermissions from '@/mixins/teamPermissions.vue'

import Button from '@/components/UI/Button.vue'
import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import SingleSelectionAutocomplete from '@/components/LocationSelector/SingleSelectionAutocomplete.vue'

import { utils } from 'frontend-js-shared'

const MAX_DESCRIPTION_CHARACTERS = 140

export default {
  name: 'TeamFormDialog',

  mixins: [mixin, teamPermissions],

  props: {
    show: Boolean,

    team: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    organizationId: {
      type: String,
      required: true,
    },

    siteId: {
      type: String,
      required: false,
    },
  },

  components: {
    Button,
    AssigneesSelector,
    SingleSelectionAutocomplete,
  },

  data() {
    return {
      MAX_DESCRIPTION_CHARACTERS,
      form: {
        ..._.cloneDeep(this.team),
        name: this.team.name || '',
        description: this.team.description || '',
        site: null,
        leaders: this.team?.leaders?.map?.(leader => leader._id) ?? [],
        members: this.team?.members?.map?.(member => member._id) ?? [],
      },
      isFormValid: false,
      isLoading: false,
      isSiteTeamWarningVisible: false,
    }
  },

  computed: {
    ...mapGetters({
      authorizedSites: 'authorizedSiteDetails',
      currentUser: 'account',
      organizationSites: 'organizationSites',
    }),

    availableSiteIds() {
      if (!this.currentUser?.isSiteAdmin) {
        return []
      }

      return (this.authorizedSites ?? []).map(site => utils.getEntityId(site))
    },

    sites() {
      const visibleSites = _.isEmpty(this.availableSiteIds)
        ? this.organizationSites
        : this.organizationSites.filter(site =>
            this.availableSiteIds.includes(utils.getEntityId(site))
          )

      return visibleSites.map(site => {
        return {
          ...site,
          subtitle: this.$t(`countries.${site.countryCodes[0]}`),
          subtitleIcon: 'mdi-earth',
        }
      })
    },

    isNew() {
      return !this.form?.id
    },

    targetTeam() {
      if (this.isNew) {
        return { site: this.form.site, leaders: this.form.leaders }
      }

      return { ...this.team, site: this.form.site }
    },

    permissions() {
      return this.getTeamPermissions({ team: this.targetTeam })
    },
  },

  async mounted() {
    this.form.site = this.isNew ? this.siteId : this.team.site
  },

  methods: {
    saveTeam() {
      this.$refs.form.validate()

      if (!this.isFormValid) return

      return this.isNew ? this.createTeam() : this.updateTeam()
    },

    async createTeam() {
      try {
        this.isLoading = true
        const response = await api.teams.create(this.organizationId, this.form)
        const team = response?.data
        await api.teams.updateMembers(
          this.organizationId,
          team.id,
          this.form.members
        )

        this.$emit('created', team)
      } catch {
        this.handleRequestError()
      } finally {
        this.isLoading = false
      }
    },

    async updateTeam() {
      try {
        this.isLoading = true

        if (this.permissions.editTeamInfo) {
          await api.teams.update(this.organizationId, this.team.id, this.form)
        }

        const response = await api.teams.updateMembers(
          this.organizationId,
          this.form.id,
          this.form.members
        )

        this.$emit('updated', response.data)
      } catch {
        this.handleRequestError()
      } finally {
        this.isLoading = false
      }
    },

    handleToggleGlobalTeam() {
      const willChangeToSiteTeam = !this.form.site

      this.form.site = this.siteId ? this.siteId : this.sites[0]._id // v-switch has a weird behavior showing the proper state when showing the confirmation dialog. Keep this line to force the proper re-render when cancelling the process of changing to a site team

      if (willChangeToSiteTeam) {
        this.isSiteTeamWarningVisible = true
        return
      }

      this.form = { ...this.form, site: null }
    },

    cancelChangeToSiteTeam() {
      this.isSiteTeamWarningVisible = false
      this.form = { ...this.form, site: null }
    },

    confirmChangeToSiteTeam() {
      this.isSiteTeamWarningVisible = false
      this.form = {
        ...this.form,
        leaders: [],
        members: [],
        site: this.siteId ? this.siteId : this.sites[0]._id,
      }
    },

    handleLeadersChange(leaders) {
      this.form = {
        ...this.form,
        leaders,
      }
    },

    handleMembersChange(members) {
      this.form = {
        ...this.form,
        members,
      }
    },

    onSiteChange(site) {
      this.form.site = site
      this.form.leaders = []
      this.form.members = []
    },
  },
}
</script>
